/**
 * FAQ 콘텐츠 — 문의 화면(SC-026)의 '자주 묻는 질문'.
 *
 * ── 왜 앱 안에 두는가 (2026-08-25) ──────────────────────────────────
 * 전에는 `GET /faqs` 로 받아 왔는데, 그 서버가 내려주는 것은 **템플릿(픽쿠리)의
 * FAQ** 였다 — 결제·배송·교환/반품·쿠지·픽쿠함·포인트·선물. peeka 에는 배송도
 * 쿠지도 없다. 화면정의서는 FAQ 소스를 "로컬/CMS 추정" 으로 열어 두었으므로
 * 여기(로컬)를 출처로 삼는다.
 *
 * 카테고리는 화면정의서 SC-026 의 여섯을 그대로 쓴다:
 *   스캔 · 시세 · 컬렉션 · 후원/결제 · 계정 · 알림
 *
 * ⚠ **지금 앱에 있는 것만 적는다.** 아직 없는 기능(개발자 후원 SC-023 등)을
 *   설명하면 FAQ 가 먼저 약속을 하게 된다. 없는 것은 "아직 없다"고만 말한다.
 * ⚠ 시세 문답은 BRAND §3.7 을 따른다 — **사실만**. 사라·팔라는 조언, 오를
 *   것이라는 암시를 넣지 않는다.
 * ⚠ 한국어만 둔다. 약관(termsContent)과 같은 방식이다 — CMS 가 붙을 때
 *   3언어를 함께 정리한다.
 */

export interface FaqEntry {
  /** 목록 안에서만 유일하면 된다 */
  id: number;
  q: string;
  /** 줄바꿈은 문단으로 그린다 */
  a: string;
}

export interface FaqCategory {
  code: string;
  name: string;
  items: FaqEntry[];
}

export const FAQ_CONTENT: FaqCategory[] = [
  {
    code: "scan",
    name: "스캔",
    items: [
      {
        id: 101,
        q: "카드를 어떻게 스캔하나요?",
        a: "하단 가운데 카메라 버튼을 누르고 카드를 화면 안에 맞추면 돼요. 여러 장을 이어서 찍을 수 있고, 다 찍은 뒤 요약 화면에서 담을 카드만 골라 컬렉션에 넣어요.",
      },
      {
        id: 102,
        q: "카드가 잘 인식되지 않아요.",
        a: "카드 전체가 화면에 들어오게 하고, 빛이 한쪽으로 반사되지 않도록 각도를 조금 바꿔 보세요. 슬리브나 탑로더에 넣은 채로도 인식되지만, 표면이 많이 긁혔거나 접힌 카드는 어려울 수 있어요.\n그래도 안 되면 검색으로 카드를 찾아 직접 담을 수 있어요.",
      },
      {
        id: 103,
        q: "스캔한 카드가 다른 카드로 인식됐어요.",
        a: "같은 그림에 번호만 다른 카드가 많아요. 요약 화면에서 카드를 눌러 번호·에디션을 확인하고, 다르면 그 자리에서 빼고 검색으로 다시 담아 주세요.",
      },
    ],
  },
  {
    code: "price",
    name: "시세",
    items: [
      {
        id: 201,
        q: "시세는 어디에서 오나요?",
        a: "국내외 거래·판매 데이터를 모아 대표 시세를 계산해요. 카드 상세의 '실거래 정보' 에서 어떤 자료를 얼마나 썼는지 볼 수 있어요.",
      },
      {
        id: 202,
        q: "'신뢰도' 는 무엇인가요?",
        a: "그 시세를 얼마나 믿을 수 있는지를 나타내요. 최근 거래가 많고 값이 고르게 모여 있을수록 높고, 거래가 드물거나 값이 크게 흩어져 있으면 낮게 표시돼요.\n신뢰도가 낮은 카드는 숫자 하나를 그대로 받아들이기보다 폭으로 보는 편이 안전해요.",
      },
      {
        id: 203,
        q: "같은 카드인데 나라마다 값이 달라요.",
        a: "언어판이 다르면 시장이 달라요. 카드 상세의 '나라별 시세 차이' 는 두 시장의 **표면 가격만** 환율로 맞춰 나란히 보여 주는 것이고, 배송비·관세·수수료는 넣지 않았어요. 실제로 들여올 때의 비용은 그보다 늘어나요.",
      },
      {
        id: 204,
        q: "시세가 '산정 불가' 로 나와요.",
        a: "쓸 만한 거래 자료가 모이지 않은 카드예요. 값을 지어내지 않고 비워 둬요. 자료가 쌓이면 자동으로 표시돼요.",
      },
    ],
  },
  {
    code: "collection",
    name: "컬렉션",
    items: [
      {
        id: 301,
        q: "컬렉션은 어떻게 만드나요?",
        a: "컬렉션 탭의 '내 컬렉션' 에서 새로 만들 수 있고, 카드 상세나 스캔 요약에서 담을 때 그 자리에서 만들 수도 있어요. 한 카드를 여러 컬렉션에 담아도 돼요.",
      },
      {
        id: 302,
        q: "컬렉션 화면의 금액은 무엇을 더한 값인가요?",
        a: "담은 카드의 지금 시세에 수량을 곱해 모두 더한 값이에요. 등급이 있는 카드는 그 등급의 시세로 계산해요. 시세를 낼 수 없는 카드는 빠져요.",
      },
      {
        id: 303,
        q: "구매가를 적는 곳이 없어졌어요.",
        a: "손익 대신 **지금 얼마인지와 얼마나 움직였는지**를 보여 드리는 쪽으로 바꿨어요. 산 값을 정확히 기억해 적기 어렵고, 등급·표면이 어긋나면 손익이 크게 틀리기 때문이에요.",
      },
    ],
  },
  {
    code: "billing",
    name: "응원·결제",
    items: [
      {
        id: 401,
        q: "유료 결제가 있나요?",
        a: "지금은 없어요. 모든 기능을 무료로 쓸 수 있고, 구독 상품도 없어요.",
      },
      {
        id: 402,
        q: "개발자를 응원할 수 있나요?",
        a: "준비하고 있어요. 아직 앱에 넣지 않았고, 열리면 이 화면에서 안내해 드릴게요.",
      },
    ],
  },
  {
    code: "account",
    name: "계정",
    items: [
      {
        id: 501,
        q: "어떤 방법으로 로그인하나요?",
        a: "Apple 과 Google 두 가지예요. 이메일·비밀번호 로그인은 없어요.",
      },
      {
        id: 502,
        q: "로그인하지 않고 담은 카드는 어떻게 되나요?",
        a: "로그인하면 기기에 있던 컬렉션과 최근 스캔이 계정으로 옮겨져요. 같은 카드는 하나로 합쳐져요.",
      },
      {
        id: 503,
        q: "탈퇴하면 담은 카드도 사라지나요?",
        a: "네. 컬렉션과 스캔 기록을 포함해 계정에 저장된 내용이 지워지고 되돌릴 수 없어요. 내 정보 맨 아래에서 탈퇴할 수 있어요.",
      },
    ],
  },
  {
    code: "notification",
    name: "알림",
    items: [
      {
        id: 601,
        q: "어떤 알림이 오나요?",
        a: "네 가지예요. 시세 변동, 컬렉션 요약, 발견(요즘 많이 모으는 카드), 공지예요. 내 정보의 '알림 설정' 에서 하나씩 끄고 켤 수 있어요.",
      },
      {
        id: 602,
        q: "알림이 오지 않아요.",
        a: "기기 설정에서 Peeka 알림이 꺼져 있는지 먼저 확인해 주세요. 앱 안의 '알림 설정' 이 켜져 있어도 기기에서 막혀 있으면 푸시가 오지 않아요. 알림 센터에는 그대로 쌓여요.",
      },
      {
        id: 603,
        q: "밤에는 알림을 받고 싶지 않아요.",
        a: "'방해 금지 모드' 를 켜면 정해 둔 시간대에는 알림을 보내지 않아요.",
      },
    ],
  },
];
